import React, { useEffect, useState, useRef } from "react";
import { Modal, Badge, Dropdown, Avatar, Spin, notification } from "antd";
import {
  BellOutlined,
  SearchOutlined,
  CloseOutlined,
  DragOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../variables/api/axiosInstance";
import CompanySelectorModal from "./CompanySelectorModal";
import { LOGGED_USER } from "../variables/constants";
import { styles, userMenuItems } from "./ui/sidebarStyles";


const NavBar = ({ filteredResults, renderSearchResults, searchValue, setSearchValue }) => {
  const navigate = useNavigate();
  const [searchVisible, setSearchVisible] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [latestNotification, setLatestNotification] = useState("");
  const [myNotifications, setMyNotifications] = useState([]);
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const dragRef = useRef({ startX: 0, startY: 0 });
  const modalRef = useRef(null);

  // Dragging handlers
  const handleMouseDown = (e) => {
    setIsDragging(true);
    const rect = modalRef.current.getBoundingClientRect();
    dragRef.current = {
      startX: e.clientX - rect.left,
      startY: e.clientY - rect.top,
    };
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragRef.current.startX,
      y: e.clientY - dragRef.current.startY,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const timeSince = (dateString) => {
    const past = new Date(dateString);
    const now = new Date();
    const diffMs = now - past;
    if (diffMs < 0) return "The date is in the future";
    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;
    const parts = [];
    if (days > 0) parts.push(`${days} jr${days > 1 ? "s" : ""}`);
    if (hours > 0) parts.push(`${hours} hr${hours > 1 ? "s" : ""}`);
    if (minutes > 0 || parts.length === 0)
      parts.push(`${minutes} min${minutes > 1 ? "s" : ""}`);
    return parts.join(", ");
  };


  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging]);

  // Keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setSearchVisible(true);
      }
      if (e.key === "Escape" && searchVisible) {
        setSearchVisible(false);
        setSearchValue("");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [searchVisible]);

  // Mise à jour dynamique des suggestions basée sur filteredResults
  useEffect(() => {
    if (searchValue?.trim()?.length >= 2) {
      setFilteredSuggestions(filteredResults || []);
    } else {
      setFilteredSuggestions([]);
    }
  }, [filteredResults, searchValue]);

  const handleInputChange = (e) => {
    console.log("Input changed:", e);
    const inputValue = e?.target?.value?.toUpperCase() || "";
    setSearchValue(inputValue);
  };

  const renderResults = () => {
    if (!filteredSuggestions.length) {
      return (
        <div style={{ textAlign: "center", padding: "48px 24px", color: "rgba(36, 33, 102, 0.5)" }}>
          <SearchOutlined style={{ fontSize: "48px", marginBottom: "16px" }} />
          <p style={{ margin: 0, fontSize: "14px" }}>Aucun résultat trouvé</p>
        </div>
      );
    }
    return renderSearchResults(filteredSuggestions);
  };

  const logout = () => {
    sessionStorage.clear();
    console.log("Déconnexion");
    navigate("/connexion");
  };

  const handleCompanySelect = (company) => {
    console.log("Compagnie sélectionnée:", company?.raisonSociale);
    sessionStorage.setItem("currentCompagnie", JSON.stringify(company));
    setVisible(false);
  };

  const [api, contextHolder] = notification.useNotification();

  const openNotificationWithIcon = (not) => {
    api.info({
      message: `${not?.titre}`,
      description: `${not?.contenu}\n\n${timeSince(not?.createdAt)}`,
      onClose: () => markAsRead(not),
    });
  };

  const markAsRead = (not) => {
    axiosInstance
      .get(`/api/notification/read/${not?.codeId}`)
      .then(() => { })
      .catch((error) => console.error("Error fetching notifications:", error));
  };

  useEffect(() => {
    axiosInstance
      .get(`/api/notification/unread/${LOGGED_USER.username}`)
      .then((response) => {
        setMyNotifications(response.data);
        response.data &&
          response.data.length > 0 &&
          latestNotification !== response.data[0] &&
          openNotificationWithIcon(response.data[0]);
        setLatestNotification(response.data[0]);
      })
      .catch((error) => {
        console.error("Error fetching notifications:", error);
      });
  }, []);

  const changeCompanyFunction = () => {
    setVisible(true);
    const codes = LOGGED_USER.agence.validAgrements.map((ag) => ag?.compagnie?.code);
    axiosInstance
      .get(`/api/organisme/all/assurances`)
      .then((res) => {
        setCompanies(res.data.filter((comp) => codes.includes(comp.code)));
        setLoading(false);
      })
      .catch((err) => console.log(err));
  }

  return (
    <>
      <nav style={styles.navbar}>
        {contextHolder}
        <div
          style={styles.searchTrigger}
          onClick={() => setSearchVisible(true)}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(74, 144, 226, 0.08)";
            e.currentTarget.style.borderColor = "rgba(74, 144, 226, 0.2)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(36, 33, 102, 0.05)";
            e.currentTarget.style.borderColor = "rgba(36, 33, 102, 0.1)";
          }}
        >
          <SearchOutlined style={{ fontSize: "18px", color: "#242166" }} />
          <span style={{ color: "rgba(36, 33, 102, 0.6)", fontSize: "14px", flex: 1 }}>
            Rechercher...
          </span>
          <span style={styles.kbd}>Ctrl+K</span>
        </div>

        {/* Right Section */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {/* Notifications */}
          <Badge count={myNotifications?.length} size="small">
            <div
              style={styles.iconButton}
              onMouseEnter={(e) => e.currentTarget.style.background = "rgba(36, 33, 102, 0.08)"}
              onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
            >
              <BellOutlined style={{ fontSize: "20px", color: "#242166" }} />
            </div>
          </Badge>

          {/* User Dropdown */}
          <Dropdown menu={{ items: userMenuItems(navigate, changeCompanyFunction, logout) }} placement="bottomRight" arrow>
            <Avatar
              style={{
                background: "linear-gradient(135deg, #4a90e2 0%, #357abd 100%)",
                cursor: "pointer",
              }}
            >
              {LOGGED_USER?.personne?.prenom?.charAt(0)}
              {LOGGED_USER?.personne?.nom?.charAt(0)}
            </Avatar>
          </Dropdown>
        </div>
      </nav>

      {/* Search Modal */}
      {searchVisible && (
        <>
          <div style={styles.overlay} onClick={() => setSearchVisible(false)} />
          <div
            ref={modalRef}
            style={{
              ...styles.searchModal,
              ...(position.x !== 0 && {
                left: position.x,
                top: position.y,
                transform: "none",
              }),
            }}
          >
            {/* Header */}
            <div style={styles.searchHeader} onMouseDown={handleMouseDown}>
              <DragOutlined style={{ color: "rgba(36, 33, 102, 0.4)", cursor: "move" }} />
              <SearchOutlined style={{ fontSize: "20px", color: "#242166" }} />
              <input
                type="text"
                placeholder="Rechercher..."
                style={styles.searchInput}
                value={searchValue}
                onChange={handleInputChange}
                autoFocus
              />
              <CloseOutlined
                style={{ fontSize: "16px", color: "rgba(36, 33, 102, 0.6)", cursor: "pointer" }}
                onClick={() => {
                  setSearchVisible(false);
                  setSearchValue("");
                }}
              />
            </div>

            {/* Results */}
            <div style={styles.resultsContainer}>{renderResults()}</div>

            {/* Footer hint */}
            <div
              style={styles.resultsContainerCard}
            >
              <span>
                <span style={styles.kbd}>↵</span> Sélectionner
              </span>
              <span>
                <span style={styles.kbd}>ESC</span> Fermer
              </span>
              <span style={{ marginLeft: "auto" }}>
                <DragOutlined /> Déplacer
              </span>
            </div>
          </div>
        </>
      )}

      {/* Company Selector Modal */}
      {loading ? (
        <Modal title="Chargement..." open={visible} closable={false} footer={null} centered>
          <Spin size="large" />
        </Modal>
      ) : (
        <CompanySelectorModal
          visible={visible}
          companies={companies}
          onSelect={handleCompanySelect}
        />
      )}

      <style>
        {`
          ${styles.resultsContainer}::-webkit-scrollbar {
            width: 6px;
          }
          ${styles.resultsContainer}::-webkit-scrollbar-track {
            background: transparent;
          }
          ${styles.resultsContainer}::-webkit-scrollbar-thumb {
            background: rgba(36, 33, 102, 0.2);
            border-radius: 3px;
          }
          ${styles.resultsContainer}::-webkit-scrollbar-thumb:hover {
            background: rgba(36, 33, 102, 0.3);
          }
        `}
      </style>
    </>
  );
};

export default NavBar;